import type { ActionOptions } from '../abstract-action'
import type { TypeCallParamsV3 } from '../../../types/http'
import type { AjaxResult } from '../../http/ajax-result'
import { AbstractAction } from '../abstract-action'
import { ApiVersion } from '../../../types/b24'

export type ActionCallV3 = ActionOptions & {
  method: string
  params?: TypeCallParamsV3
  requestId?: string
}

/**
 * Calls the Bitrix24 REST API method `restApi:v3`
 *
 * Executes a single REST API request against the v3 HTTP client and returns the raw response.
 * Unlike its v2 counterpart `CallV2`, it routes through the v3 endpoint without a client-side
 * method allowlist — the server validates the method and returns `METHODNOTFOUNDEXCEPTION` for
 * unknown ones. Like `CallV2`, it makes exactly one HTTP call with no pagination or batching.
 */
export class CallV3 extends AbstractAction {
  /**
   * Calls the Bitrix24 REST API method.
   *
   * @template T - The expected data type in the response (default is `unknown`).
   *
   * @param {ActionCallV3} options - parameters for executing the request.
   *     - `method: string` - REST API method name (eg: `crm.item.get`)
   *     - `params?: TypeCallParamsV3` - Parameters for calling the method.
   *     - `requestId?: string` - Unique request identifier for tracking. Used for query deduplication and debugging.
   *
   * @returns {Promise<AjaxResult<T>>} A promise that resolves to the result of an REST API call.
   *
   * @example
   * interface TaskItem { id: number, title: string }
   * const response = await b24.actions.v3.call.make<{ item: TaskItem }>({
   *   method: 'tasks.task.get',
   *   params: { id: 123, select: ['id', 'title'] },
   *   requestId: 'task-123'
   * })
   * if (!response.isSuccess) {
   *   throw new Error(`Problem: ${response.getErrorMessages().join('; ')}`)
   * }
   * console.log(response.getData().result.item.title)
   */
  public override async make<T = unknown>(options: ActionCallV3): Promise<AjaxResult<T>> {
    // No client-side allowlist: the method is sent straight to the v3 endpoint
    // and the server decides (an unknown method returns METHODNOTFOUNDEXCEPTION).
    const params = options.params || {}
    return this._b24.getHttpClient(ApiVersion.v3).call<T>(options.method, params, options.requestId)
  }
}
